import type { TableColumn } from 'uikit';

import type React from 'react';
import { useMemo } from 'react';

import type { ItemAction } from '../../DropdownContext';
import { HierarchyColumnCell } from '../components/HierarchyColumnCell';
import { HierarchyColumnHeader } from '../components/HierarchyColumnHeader';
import { MenuColumnCell } from '../components/MenuColumnCell';
import { MenuColumnHeader } from '../components/MenuColumnHeader';
import type { ITableLikeTreeItem } from '../context';

interface IUseTableTreeColumns {
    isContextMenuAvailable?: boolean;
    setContextMenuInHierarchyColumn?: boolean;
    isRowCheckAvailable?: boolean;
    menuItems?: ItemAction[];
    hierarchyColumnTitle: string | React.ReactNode;
    showExpandAllSwitchInHierarchyHeader?: boolean;
    additionalColumns?: TableColumn<any>[];
    showClearNotifications?: boolean;
    showCreateNewObjectButton?: boolean;
    onAddChildExternallyCall?: (
        addChild: (child: ITableLikeTreeItem, parent?: ITableLikeTreeItem) => any,
        parent?: ITableLikeTreeItem,
    ) => any;
    isHeaderCheckboxDisabled?: boolean;
    tableTreeColumnRightSide?: React.ReactNode;
    tableTreeColumnLeftSide?: React.ReactNode;
    menuItemsGetter?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => ItemAction[];
    isDragOperationDisabled?: boolean;
    isDragButtonHidden?: boolean;
    isDragButtonObjHidden?: boolean;
    menuColumnWidth?: number;
    menuColumnAlign?: 'left' | 'center' | 'right';
    noMenuColumn?: boolean;
    isAllMenuDisabledCallback?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => boolean;
}

export const useTableTreeColumns = (props: IUseTableTreeColumns): TableColumn<any>[] => {
    const {
        isRowCheckAvailable,
        isContextMenuAvailable,
        setContextMenuInHierarchyColumn,
        menuItems,
        menuItemsGetter,
        hierarchyColumnTitle,
        showExpandAllSwitchInHierarchyHeader,
        additionalColumns = [],
        showClearNotifications,
        showCreateNewObjectButton,
        onAddChildExternallyCall,
        isHeaderCheckboxDisabled,
        tableTreeColumnRightSide,
        tableTreeColumnLeftSide,
        isDragOperationDisabled,
        isDragButtonHidden,
        isDragButtonObjHidden,
        menuColumnWidth = 70,
        menuColumnAlign,
        noMenuColumn,
        isAllMenuDisabledCallback,
    } = props;

    const columnsCount = useMemo(
        () => 2 + additionalColumns.reduce((acc, c) => acc + (c?.columns?.length || 1), 0),
        [additionalColumns],
    );

    return useMemo(
        () => [
            ...(noMenuColumn
                ? []
                : [
                      {
                          title: isRowCheckAvailable ? (
                              <MenuColumnHeader
                                  setContextMenuInHierarchyColumn={setContextMenuInHierarchyColumn}
                                  disabled={isHeaderCheckboxDisabled}
                              />
                          ) : (
                              ''
                          ),
                          accessor: 'menu-column',
                          width: menuColumnWidth,
                          align: menuColumnAlign,
                          renderCell: (treeItem: any) => (
                              <MenuColumnCell
                                  isCheckboxAvailable={isRowCheckAvailable}
                                  isContextMenuAvailable={isContextMenuAvailable && !setContextMenuInHierarchyColumn}
                                  treeItem={treeItem}
                                  menuItems={menuItems}
                                  menuItemsGetter={menuItemsGetter}
                                  columnsCount={columnsCount}
                                  isDragAndDropDisabled={isDragOperationDisabled}
                                  isDragButtonObjHidden={isDragButtonObjHidden}
                                  isDragButtonHidden={isDragButtonHidden}
                                  isAllMenuDisabledCallback={isAllMenuDisabledCallback}
                              />
                          ),
                      },
                  ]),
            {
                title: (
                    <HierarchyColumnHeader
                        title={hierarchyColumnTitle}
                        showExpandAllSwitch={showExpandAllSwitchInHierarchyHeader}
                        showClearNotifications={showClearNotifications}
                        showCreateNewObjectButton={showCreateNewObjectButton}
                        onAddChildExternallyCall={onAddChildExternallyCall}
                        rightSide={tableTreeColumnRightSide}
                        leftSide={tableTreeColumnLeftSide}
                    />
                ),
                accessor: 'name',
                renderCell: (treeItem: any) => (
                    <HierarchyColumnCell
                        item={treeItem}
                        menuItems={menuItems}
                        menuItemsGetter={menuItemsGetter}
                        isContextMenuAvailable={isContextMenuAvailable && setContextMenuInHierarchyColumn}
                    />
                ),
            },
            ...additionalColumns,
        ],
        [
            additionalColumns,
            columnsCount,
            hierarchyColumnTitle,
            isAllMenuDisabledCallback,
            isContextMenuAvailable,
            isDragButtonHidden,
            isDragButtonObjHidden,
            isDragOperationDisabled,
            isHeaderCheckboxDisabled,
            isRowCheckAvailable,
            menuColumnAlign,
            menuColumnWidth,
            menuItems,
            menuItemsGetter,
            noMenuColumn,
            onAddChildExternallyCall,
            setContextMenuInHierarchyColumn,
            showClearNotifications,
            showCreateNewObjectButton,
            showExpandAllSwitchInHierarchyHeader,
            tableTreeColumnLeftSide,
            tableTreeColumnRightSide,
        ],
    );
};
